import { CommonModule } from '@angular/common';
import { Component, OnInit, AfterViewInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { RippleModule } from 'primeng/ripple';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { AuthService } from '../../shared/services/auth.service';
import { ThemeService } from '../../shared/services/theme.service';
import { environment } from '../../../environments/environment';

export type AuthRole = 'candidate' | 'panel' | 'corporates';

interface RoleInfo {
  key: AuthRole;
  displayName: string;
  tagline: string;
  description: string;
  icon: string;
  accentClass: string;
}

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ButtonModule,
    InputTextModule,
    PasswordModule,
    RippleModule,
    ToastModule,
  ],
  providers: [MessageService],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
})
export class LoginComponent implements OnInit, AfterViewInit {
  private authService = inject(AuthService);
  private router = inject(Router);
  private themeService = inject(ThemeService);
  private messageService = inject(MessageService);

  email = '';
  password = '';
  loading = signal(false);
  errorMsg = signal('');
  showPassword = signal(false);
  role = signal<AuthRole>('candidate');

  readonly isDark = this.themeService.isDark;

  readonly roleInfoMap: Record<AuthRole, RoleInfo> = {
    candidate: {
      key: 'candidate',
      displayName: 'Candidate',
      tagline: 'Land Your Dream Job',
      description:
        'Practice with real industry experts and ace your next interview with confidence.',
      icon: 'pi-briefcase',
      accentClass: 'auth-panel--candidate',
    },
    panel: {
      key: 'panel',
      displayName: 'Panelist',
      tagline: 'Share Your Expertise',
      description: 'Evaluate top talent, shape careers, and grow your professional network.',
      icon: 'pi-star',
      accentClass: 'auth-panel--panel',
    },
    corporates: {
      key: 'corporates',
      displayName: 'Employer',
      tagline: 'Hire Smarter, Faster',
      description: 'Streamline your hiring with structured, expert-led interviews at scale.',
      icon: 'pi-building',
      accentClass: 'auth-panel--corporate',
    },
  };

  get currentRole(): RoleInfo {
    return this.roleInfoMap[this.role()];
  }

  /** Panelist/Employer sign-ins use a work email; candidates can use any email. */
  get emailPlaceholder(): string {
    return this.role() === 'candidate' ? 'you@example.com' : 'Enter your work email';
  }

  ngOnInit(): void {
    const stored = localStorage.getItem('ip-auth-role') as AuthRole | null;
    if (stored && this.roleInfoMap[stored]) {
      this.role.set(stored);
    }
  }

  // AfterViewInit, not OnInit: <p-toast> (a child in this component's own
  // template) only subscribes to MessageService inside ITS ngOnInit, which
  // runs AFTER this component's ngOnInit. Calling add() there would fire
  // before the toast is listening and the message would be silently lost.
  ngAfterViewInit(): void {
    if (this.authService.consumeLogoutToast()) {
      setTimeout(() => {
        this.messageService.add({
          severity: 'success',
          summary: 'Logged Out',
          detail: 'You have been logged out successfully.',
          life: 3000,
        });
      });
    }
  }

  toggleTheme(): void {
    this.themeService.toggle();
  }

  togglePassword(): void {
    this.showPassword.set(!this.showPassword());
  }

  onLogin(): void {
    if (!this.email || !this.password) {
      this.errorMsg.set('Please enter your email and password.');
      return;
    }
    this.loading.set(true);
    this.errorMsg.set('');
    // So a later logout() can bring the user back to this same role tab.
    localStorage.setItem('ip-auth-role', this.role());

    this.authService.login(this.email, this.password, this.role()).subscribe({
      next: (res) => {
        this.authService.storeSession(res);

        // Check whether this user has an Interpanely profile.
        // If none exists, send them to complete-profile to finish setup.
        this.authService.checkProfileStatus(res.access_token).subscribe({
          next: (status) => {
            this.loading.set(false);
            if (status.exists) {
              this._redirectByRole(status.profileType);
            } else {
              this._prepareProfileSetup(res.access_token);
              this.router.navigate(['/auth/complete-profile']);
            }
          },
          error: () => {
            // profile-status call failed, fall back to normal redirect
            this.loading.set(false);
            this._redirectByRole();
          },
        });
      },
      error: (err) => {
        console.error('Login failed', err);
        this.loading.set(false);
        // 403 = credentials were fine but the backend rejected this
        // account for the selected role tab (see RegisterController#login);
        // surface its specific message instead of a generic one.
        this.errorMsg.set(this._extractError(err, 'Invalid credentials. Please try again.'));
      },
    });
  }

  /** Backend returns { message } (JSON) for ResponseStatusException-based rejections. */
  private _extractError(err: any, fallback: string): string {
    const body = err?.error;
    if (typeof body === 'string') {
      return body.trim() || fallback;
    }
    return body?.message || body?.error || fallback;
  }

  /** Populates localStorage so complete-profile can create the UserProfile row. */
  private _prepareProfileSetup(accessToken: string): void {
    localStorage.setItem('verifyEmail', this.email);
    // 'social' tells complete-profile the auth account already exists —
    // only the UserProfile row needs to be created, not a new account.
    localStorage.setItem('ip-auth-provider', 'social');
    localStorage.setItem('ip-auth-role', this.role());
  }

  loginWithGoogle(): void {
    localStorage.setItem('ip-auth-role', this.role());
    const google = (window as any).google;
    if (!google?.accounts?.id) {
      this.errorMsg.set('Google Sign-In is unavailable. Please try again shortly.');
      return;
    }
    google.accounts.id.initialize({
      client_id: environment.googleClientId,
      callback: (response: { credential: string }) => {
        this._handleGoogleCredential(response.credential);
      },
      cancel_on_tap_outside: false,
    });
    google.accounts.id.prompt();
  }

  private _handleGoogleCredential(idToken: string): void {
    this.loading.set(true);
    this.errorMsg.set('');
    this.authService.googleAuth(idToken, this.role()).subscribe({
      next: (res) => {
        this.authService.storeSession(res);
        this.loading.set(false);
        this._redirectByRole();
      },
      error: (err) => {
        this.loading.set(false);
        this.errorMsg.set(this._extractError(err, 'Google sign-in failed. Please try again.'));
      },
    });
  }

  goToPhoneLogin(): void {
    localStorage.setItem('ip-auth-role', this.role());
    this.router.navigate(['/auth/phone-login']);
  }

  goToRegister(): void {
    localStorage.setItem('ip-auth-role', this.role());
    this.router.navigate(['/auth/register']);
  }

  forgotPassword(): void {
    localStorage.setItem('authFlow', 'forgotPassword');
    localStorage.setItem('ip-auth-role', this.role());
    this.router.navigate(['/auth/register']);
  }

  /**
   * Redirects based on the user's ACTUAL profile type (from the backend),
   * validated against the login tab they picked.
   *
   * Strict, one-to-one match: a candidate account may only enter through
   * the Candidate tab, a panelist account only through the Panelist tab,
   * and a corporate account only through the Employer tab. The backend
   * (RegisterController#login + LoginRoleValidator) already enforces this
   * for email/password logins before a session is ever created, so in
   * practice this branch shouldn't fire for that path, it's kept as
   * defense-in-depth, and it's still the only check for the Google/
   * LinkedIn SSO path, where `status.profileType` comes back after the
   * session already exists.
   */
  private _redirectByRole(actualProfileType?: string): void {
    const tab = this.role();
    const type = (actualProfileType || '').toUpperCase();

    const tabToExpectedType: Record<AuthRole, string> = {
      candidate: 'CANDIDATE',
      panel: 'PANELIST',
      corporates: 'CORPORATE',
    };
    const expected = tabToExpectedType[tab];

    if (
      type &&
      expected &&
      type !== expected &&
      !(expected === 'CORPORATE' && type === 'CORPORATES')
    ) {
      this._rejectLogin(
        `This account is not a ${this.roleInfoMap[tab].displayName} account. Please log in from the correct tab for your account type.`,
      );
      return;
    }

    if (tab === 'corporates') {
      this.router.navigate(['/app/employer']);
    } else if (tab === 'panel') {
      this.router.navigate(['/app/panelist']);
    } else {
      this.router.navigate(['/app/candidate']);
    }
  }

  /** Clears the just-created session and shows why the login was refused. */
  private _rejectLogin(message: string): void {
    this.authService.clearAllAuthStorage();
    this.errorMsg.set(message);
  }
}
